import React, { useMemo } from 'react';
import type { TripResponseArgs } from '@this/domain/trip/trip2';
import type { TravelerInformationResponseArgs } from '@this/domain/traveler_information';
import type { TransportElementResponseArgs } from '@this/domain/transport_element2';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import type { ProposalBundle } from '../api/proposalBundles';
import LatestProposals from './LatestProposals';
import { ORDER_ITEM_CATEGORY, isFlightCategory } from '../types/orderItemCategory';
import TravelerInfoDisplay, { TRAVELER_DISPLAY_TYPE } from './TravelerInfoDisplay';

interface Props {
  trip: TripResponseArgs;
  proposalBundles?: ProposalBundle[];
}

interface ReservationInfoSectionProps {
  styles: ReturnType<typeof getStyles>;
  hotelTravelerInfo: TravelerInformationResponseArgs | null;
  flightTravelerInfo: TravelerInformationResponseArgs | null;
  shinkansenTravelerInfo: TravelerInformationResponseArgs | null;
  hasHotelReservation: boolean;
  hasFlightTransport: boolean;
  hasShinkansenTransport: boolean;
}

const ReservationInfoSection = ({
  styles,
  hotelTravelerInfo,
  flightTravelerInfo,
  shinkansenTravelerInfo,
  hasHotelReservation,
  hasFlightTransport,
  hasShinkansenTransport
}: ReservationInfoSectionProps) => {
  const hasNoReservation =
    !(hasHotelReservation && hotelTravelerInfo) &&
    !(hasFlightTransport && flightTravelerInfo) &&
    !(hasShinkansenTransport && shinkansenTravelerInfo);

  if (hasNoReservation) {
    return (
      <div style={styles.collapsible}>
        <div style={styles.collapsibleHeader}>予約情報</div>
        <div style={styles.collapsibleBody}>予約情報がここに表示されます。</div>
      </div>
    );
  }

  return (
    <>
      {hasHotelReservation && hotelTravelerInfo && (
        <div style={styles.collapsible}>
          <div style={styles.collapsibleHeader}>宿泊情報</div>
          <div style={styles.collapsibleBody}>
            <TravelerInfoDisplay travelerInfo={hotelTravelerInfo} displayType={TRAVELER_DISPLAY_TYPE.HOTEL} />
          </div>
        </div>
      )}
      {hasFlightTransport && flightTravelerInfo && (
        <div style={styles.collapsible}>
          <div style={styles.collapsibleHeader}>航空券搭乗情報</div>
          <div style={styles.collapsibleBody}>
            <TravelerInfoDisplay travelerInfo={flightTravelerInfo} displayType={TRAVELER_DISPLAY_TYPE.FLIGHT} />
          </div>
        </div>
      )}
      {hasShinkansenTransport && shinkansenTravelerInfo && (
        <div style={styles.collapsible}>
          <div style={styles.collapsibleHeader}>新幹線搭乗情報</div>
          <div style={styles.collapsibleBody}>
            <TravelerInfoDisplay
              travelerInfo={shinkansenTravelerInfo}
              displayType={TRAVELER_DISPLAY_TYPE.SHINKANSEN}
            />
          </div>
        </div>
      )}
    </>
  );
};

const mergeTravelerInfos = (
  travelers: TravelerInformationResponseArgs[]
): TravelerInformationResponseArgs | null => {
  if (travelers.length === 0) return null;
  if (travelers.length === 1) return travelers[0];

  const initial: Record<string, unknown> = { ...travelers[0] };
  return travelers.slice(1).reduce((merged, t) => {
    Object.entries(t).reduce((acc, [key, value]) => {
      if (value !== null && value !== undefined && value !== '' && !acc[key]) {
        acc[key] = value;
      }
      return acc;
    }, merged);
    return merged;
  }, initial) as TravelerInformationResponseArgs;
};

const InfoPanel = ({ trip, proposalBundles = [] }: Props) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const tripOverview = buildTripOverview(trip);
  const includeTax = false;

  const orderItems = useMemo(() => trip.current_order?.order_items || [], [trip.current_order?.order_items]);

  const hotelTravelerInfo = useMemo((): TravelerInformationResponseArgs | null => {
    const hotelItem = orderItems.find(item => item.order_item_category === ORDER_ITEM_CATEGORY.HOTEL);
    const travelers = hotelItem?.traveler_informations || [];
    return mergeTravelerInfos(travelers);
  }, [orderItems]);

  const flightTravelerInfo = useMemo((): TravelerInformationResponseArgs | null => {
    const flightItem = orderItems.find(item => isFlightCategory(item.order_item_category));
    const travelers = flightItem?.traveler_informations || [];
    return mergeTravelerInfos(travelers);
  }, [orderItems]);

  const shinkansenTravelerInfo = useMemo((): TravelerInformationResponseArgs | null => {
    const shinkansenItem = orderItems.find(item => item.order_item_category === ORDER_ITEM_CATEGORY.SHINKANSEN);
    const travelers = shinkansenItem?.traveler_informations || [];
    return mergeTravelerInfos(travelers);
  }, [orderItems]);

  const transportInfo = useMemo((): TransportElementResponseArgs[] => {
    return orderItems.flatMap(item => item.transports || []);
  }, [orderItems]);

  const hasHotelReservation = useMemo((): boolean => {
    return orderItems.some(item => (item.hotels || []).length > 0);
  }, [orderItems]);

  const hasFlightTransport = useMemo((): boolean => {
    return transportInfo.some(t => isFlightCategory(t.transport_type));
  }, [transportInfo]);

  const hasShinkansenTransport = useMemo((): boolean => {
    return transportInfo.some(t => t.transport_type === ORDER_ITEM_CATEGORY.SHINKANSEN);
  }, [transportInfo]);

  return (
    <div style={styles.container}>
      <section style={styles.section}>
        <h3 style={styles.sectionTitle}>このチャットの最新の提案内容</h3>
        <LatestProposals bundles={proposalBundles} includeTax={includeTax} />
      </section>
      <section style={styles.section}>
        <div style={styles.sectionHeader}>
          <h3 style={styles.sectionTitle}>出張申請に関する情報</h3>
          <button type="button" style={styles.linkButton}>
            編集
          </button>
        </div>
        <ul style={styles.list}>
          <li>出張先: {tripOverview.destination}</li>
          <li>出張の目的: {tripOverview.purpose}</li>
          <li>社内管理番号: {tripOverview.internalNumber}</li>
          <li>費用負担部署: {tripOverview.department}</li>
          <li>プロジェクト: {tripOverview.project}</li>
        </ul>
      </section>
      <section style={styles.section}>
        <div style={styles.sectionHeader}>
          <h3 style={styles.sectionTitle}>予約手配に関する情報</h3>
          <button type="button" style={styles.linkButton}>
            編集
          </button>
        </div>
        <ReservationInfoSection
          styles={styles}
          hotelTravelerInfo={hotelTravelerInfo}
          flightTravelerInfo={flightTravelerInfo}
          shinkansenTravelerInfo={shinkansenTravelerInfo}
          hasHotelReservation={hasHotelReservation}
          hasFlightTransport={hasFlightTransport}
          hasShinkansenTransport={hasShinkansenTransport}
        />
      </section>
    </div>
  );
};

const getStyles = (theme: AITTheme) => ({
  container: {
    width: '320px',
    padding: '20px',
    backgroundColor: theme.contentBgColor,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '16px',
    overflowY: 'auto' as const,
    flexShrink: 0,
    minHeight: 0
  },
  section: {
    border: `1px solid ${theme.grayBorderColor}`,
    borderRadius: '8px',
    padding: '16px',
    backgroundColor: theme.whiteColor
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: 600 as const,
    margin: 0
  },
  list: {
    listStyle: 'none' as const,
    padding: 0,
    margin: '12px 0 0',
    lineHeight: 1.8,
    fontSize: '13px'
  },
  linkButton: {
    border: 'none',
    background: 'none',
    color: theme.linkColor,
    cursor: 'pointer'
  },
  collapsible: {
    border: `1px solid ${theme.grayBorderColor}`,
    borderRadius: '6px',
    marginTop: '12px'
  },
  collapsibleHeader: {
    padding: '10px 12px',
    backgroundColor: theme.contentBgColor,
    fontWeight: 600 as const
  },
  collapsibleBody: {
    padding: '12px',
    color: theme.grayTextColor,
    fontSize: '13px'
  }
});

const EMPTY_VALUE = '未記入';

const buildTripOverview = (trip: TripResponseArgs) => {
  const destination = trip.final_destination || EMPTY_VALUE;
  const purpose = trip.purpose || EMPTY_VALUE;
  const internalNumber = trip.internal_number || EMPTY_VALUE;
  const departmentName = trip.charging_departments?.[0]?.name;
  const projectName = trip.projects?.[0]?.name;

  return {
    destination,
    purpose,
    internalNumber,
    department: departmentName || EMPTY_VALUE,
    project: projectName || EMPTY_VALUE
  };
};

export default InfoPanel;
